import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Avatar } from "../../commons/Avatar.js";

interface Props {
  name: string;
  // Already joined with " · "; omitted when there is nothing to show.
  details: string | null;
  badge: "crm" | "new";
  // The card's content is a button: activating it lets the user change the
  // client (the modal opens the contact select in its place).
  onEdit: () => void;
  onClear: () => void;
  disabled?: boolean;
}

// Legal forms say nothing about who the client is, so they never make up the
// initials ("Acme GmbH" reads "AC", not "AG"). Compared without punctuation.
const LEGAL_FORMS = new Set([
  "ag",
  "co",
  "ek",
  "ev",
  "gbr",
  "gmbh",
  "inc",
  "kg",
  "ltd",
  "mbh",
  "ohg",
  "se",
  "ug",
]);

// First and last word's initial for a name of several words, the first two
// letters for a single word.
export function clientInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .map(word => word.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(word => word && !LEGAL_FORMS.has(word.toLowerCase()));
  const first = words[0];
  if (!first) return name.trim().substring(0, 2);
  const last = words[words.length - 1];
  return last && words.length > 1
    ? `${first.substring(0, 1)}${last.substring(0, 1)}`
    : first.substring(0, 2);
}

// The resolved client of the quick-accept modal, shown in place of the client
// select: who the client is and whether it already exists in the CRM. It
// holds no client logic; the modal decides what to show and what clearing does.
export function ClientCard({
  name,
  details,
  badge,
  onEdit,
  onClear,
  disabled = false,
}: Props) {
  const { t } = useTranslate(["ProjectCreate"]);

  return (
    // A fieldset with its legend on the border imitates the neighbouring
    // outlined fields, so the card lines up with them.
    <Box
      component="fieldset"
      sx={{
        m: 0,
        px: 1.5,
        pt: 0.5,
        pb: 1.5,
        minWidth: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography
        component="legend"
        variant="caption"
        sx={{ px: 0.5, color: "text.secondary" }}
      >
        {`${t("Client / contractee", { ns: "ProjectCreate" })} *`}
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <ButtonBase
          onClick={onEdit}
          disabled={disabled}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            justifyContent: "flex-start",
            textAlign: "left",
            gap: 1.5,
            borderRadius: 1,
            mx: -0.5,
            px: 0.5,
            py: 0.5,
          }}
        >
          <Avatar size="m" initials={clientInitials(name)} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
              {name}
            </Typography>
            {details && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
                noWrap
              >
                {details}
              </Typography>
            )}
          </Box>
        </ButtonBase>
        <Chip
          size="small"
          color={badge === "crm" ? "success" : "info"}
          label={
            badge === "crm"
              ? t("CRM contact", { ns: "ProjectCreate" })
              : t("New contact", { ns: "ProjectCreate" })
          }
        />
        <IconButton
          size="small"
          aria-label={t("Clear client", { ns: "ProjectCreate" })}
          onClick={onClear}
          disabled={disabled}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
